/**
 * MinistrareIA - Onboarding Agent Service
 * Configuração das seções, transcrição de áudio e helpers
 * 
 * UPDATED: 
 * - Objetivo "Agendar" substituído por "Assistente do Dia a Dia"
 * - Sub-perguntas dinâmicas por objetivo selecionado
 */

import { 
  Building2, Car, ShoppingBag, Headphones,
  Target, Smile, Zap, Award, MessageCircle,
  Shield, Clock, Ban, DollarSign, Bot
} from 'lucide-react';

export const BUSINESS_CATEGORIES = [
  { 
    id: 'real_estate', 
    label: 'Imobiliária', 
    description: 'Corretores e imobiliárias',
    icon: Building2,
    color: '#3B82F6'
  },
  { 
    id: 'automotive', 
    label: 'Automotivo', 
    description: 'Concessionárias e revendas',
    icon: Car,
    color: '#EF4444'
  },
  { 
    id: 'products', 
    label: 'Produtos', 
    description: 'E-commerce e varejo',
    icon: ShoppingBag,
    color: '#10B981'
  },
  { 
    id: 'support', 
    label: 'Atendimento', 
    description: 'SAC e suporte ao cliente',
    icon: Headphones,
    color: '#8B5CF6'
  }
];

// Sub-perguntas por objetivo
export const OBJECTIVE_QUESTIONS = {
  sales: [
    {
      id: 'sales_q1',
      question: 'Qual é o produto ou serviço principal que o agente deve vender?',
      placeholder: 'Ex: Apartamentos de 2 e 3 quartos na zona sul, carros seminovos até 80 mil...'
    },
    {
      id: 'sales_q2', 
      question: 'Quais são as objeções mais comuns dos clientes e como contorná-las?',
      placeholder: 'Ex: "Está muito caro" - mostrar condições de financiamento, "Preciso pensar" - criar urgência...'
    },
    {
      id: 'sales_q3',
      question: 'Qual é o diferencial competitivo que o agente deve destacar?',
      placeholder: 'Ex: Melhor custo-benefício da região, garantia estendida, entrega em 24h...'
    }
  ],
  qualify: [
    {
      id: 'qualify_q1',
      question: 'Quais critérios definem um lead qualificado para você?',
      placeholder: 'Ex: Ter orçamento acima de X, estar buscando imóvel nos próximos 3 meses, ser decisor...'
    },
    {
      id: 'qualify_q2',
      question: 'Quais perguntas o agente deve fazer para qualificar o lead?',
      placeholder: 'Ex: Qual seu orçamento? Quando pretende comprar? Já visitou outros imóveis?...'
    },
    {
      id: 'qualify_q3',
      question: 'O que o agente deve fazer quando identificar um lead qualificado?',
      placeholder: 'Ex: Agendar visita, transferir para vendedor, enviar catálogo personalizado...'
    }
  ],
  support: [
    {
      id: 'support_q1',
      question: 'Quais são os problemas ou dúvidas mais comuns dos clientes?',
      placeholder: 'Ex: Status do pedido, prazo de entrega, como trocar produto, formas de pagamento...'
    },
    {
      id: 'support_q2',
      question: 'Em quais situações o agente deve transferir para um humano?',
      placeholder: 'Ex: Reclamações graves, pedido de cancelamento, problemas técnicos complexos...'
    },
    {
      id: 'support_q3',
      question: 'Quais informações o agente precisa coletar do cliente?',
      placeholder: 'Ex: Número do pedido, CPF, e-mail cadastrado, descrição do problema...'
    }
  ],
  assistant: [
    {
      id: 'assistant_q1',
      question: 'Quais tipos de mensagens você recebe com mais frequência?',
      placeholder: 'Ex: Perguntas sobre preços, pedidos de orçamento, dúvidas sobre produtos, agendamentos...'
    },
    {
      id: 'assistant_q2',
      question: 'Como o agente deve responder quando não souber algo?',
      placeholder: 'Ex: Informar que vai verificar e retornar, pedir para aguardar, transferir para você...'
    },
    {
      id: 'assistant_q3',
      question: 'Quais informações básicas o agente deve ter sempre à mão?',
      placeholder: 'Ex: Horário de funcionamento, endereço, formas de pagamento, prazo de entrega...'
    }
  ]
};

export const ONBOARDING_SECTIONS = {
  // Seção 1: Identidade (obrigatória)
  identity: {
    id: 'identity',
    title: 'Identidade do Negócio',
    botMessage: 'Vamos começar! Me conte sobre seu negócio. Qual é o nome da sua empresa e o que vocês fazem?',
    inputType: 'text_audio',
    required: true,
    placeholder: 'Ex: Somos a ImobPlus, uma imobiliária especializada em imóveis de alto padrão em São Paulo...',
    minLength: 20,
    helpText: 'Fale sobre sua empresa, produtos/serviços principais e diferenciais'
  },

  // Seção 2: Tom de voz (obrigatória)
  tone_of_voice: {
    id: 'tone_of_voice',
    title: 'Tom de Voz',
    botMessage: 'Ótimo! Como você quer que seu agente se comunique com os clientes?',
    inputType: 'options_custom',
    required: true,
    options: [
      { id: 'formal', label: 'Formal e Profissional', description: 'Linguagem corporativa e respeitosa', icon: Award },
      { id: 'friendly', label: 'Amigável e Acessível', description: 'Próximo e descontraído', icon: Smile },
      { id: 'persuasive', label: 'Persuasivo e Vendedor', description: 'Foco em conversão', icon: Zap },
      { id: 'consultive', label: 'Consultivo e Educativo', description: 'Explica e orienta', icon: MessageCircle }
    ],
    customPlaceholder: 'Descreva o tom de voz personalizado...'
  },

  // Seção 3: Objetivo (opcional) - Agora com sub-perguntas
  objective: {
    id: 'objective',
    title: 'Objetivo Principal',
    botMessage: 'Qual é o principal objetivo do seu agente? (Você pode pular se preferir)',
    inputType: 'options',
    required: false,
    hasFollowUp: true,
    options: [
      { id: 'sales', label: 'Vender', description: 'Converter leads em vendas', icon: DollarSign },
      { id: 'qualify', label: 'Qualificar Leads', description: 'Identificar oportunidades', icon: Target },
      { id: 'support', label: 'Atender/Suportar', description: 'Resolver dúvidas e problemas', icon: Headphones },
      { id: 'assistant', label: 'Assistente do Dia a Dia', description: 'Automatizar respostas', icon: Bot }
    ],
    skipText: 'Pular esta etapa'
  },

  // Seção 4: Regras/Limites (obrigatória)
  rules: {
    id: 'rules',
    title: 'Regras e Limites',
    botMessage: 'Importante! O que seu agente NÃO deve fazer ou falar?',
    inputType: 'multiselect_text',
    required: true,
    options: [
      { id: 'no_prices', label: 'Não informar preços', description: 'Direcionar para vendedor', icon: DollarSign },
      { id: 'no_competitors', label: 'Não falar de concorrentes', description: 'Evitar comparações', icon: Ban },
      { id: 'no_promises', label: 'Não fazer promessas', description: 'Evitar compromissos sem autorização', icon: Shield },
      { id: 'business_hours', label: 'Respeitar horário comercial', description: 'Não agendar fora do expediente', icon: Clock }
    ],
    textPlaceholder: 'Adicione outras regras específicas do seu negócio...'
  },

  // Seção 5: FAQ (opcional) - Limite de 15
  faq: {
    id: 'faq',
    title: 'Perguntas Frequentes',
    botMessage: 'Quais são as perguntas mais comuns que seus clientes fazem? (Opcional)',
    inputType: 'faq_cards',
    required: false,
    maxItems: 15,
    placeholder: {
      question: 'Pergunta frequente...',
      answer: 'Resposta padrão...'
    },
    skipText: 'Pular esta etapa'
  },

  // Seção 6: Escalonamento Humano (opcional)
  human_handoff: {
    id: 'human_handoff',
    title: 'Atendimento Humano',
    botMessage: 'Em quais situações o agente deve parar e chamar você ou sua equipe para responder? (Opcional)',
    inputType: 'text_only',
    required: false,
    placeholder: 'Ex: Quando o cliente pedir desconto, reclamar de algo, quiser cancelar, perguntar algo que o agente não sabe...',
    minLength: 15,
    skipText: 'Pular esta etapa'
  },

  // Seção 7: Personalização Extra (opcional) - Só texto, max 200
  customization: {
    id: 'customization',
    title: 'Personalização Extra',
    botMessage: 'Tem mais alguma coisa que você gostaria de adicionar sobre seu negócio ou como o agente deve se comportar? (Opcional)',
    inputType: 'text_only',
    required: false,
    placeholder: 'Ex: Sempre mencionar nossa garantia de 30 dias, oferecer desconto para pagamento à vista...',
    maxLength: 200,
    minLength: 10,
    skipText: 'Finalizar sem adicionar'
  }
};

// Ordem das seções (7 seções base - sub-perguntas são dinâmicas)
export const SECTION_ORDER = [
  'identity',
  'tone_of_voice',
  'objective',
  'rules',
  'faq',
  'human_handoff',
  'customization'
];

// ============================================
// FUNÇÕES DE API
// ============================================

const INFINITAGENTS_API_URL = process.env.REACT_APP_INFINITAGENTS_API_URL || 'https://api.infinitagents.com';

/**
 * Transcreve áudio usando a API do InfinitAgents
 */
export const transcribeAudio = async (audioBlob) => {
  try {
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');
    formData.append('language', 'pt');

    const response = await fetch(`${INFINITAGENTS_API_URL}/api/v1/transcribe`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Erro na transcrição');
    }

    const result = await response.json();
    return result.text || null;
  } catch (error) {
    console.error('Erro ao transcrever áudio:', error);
    return null;
  }
};

/**
 * Monta o prompt final a partir das respostas
 */
export const buildFinalPrompt = (category, sections) => {
  const categoryNames = {
    real_estate: 'imobiliária',
    automotive: 'automotivo',
    products: 'e-commerce',
    support: 'atendimento ao cliente'
  };

  const toneNames = {
    formal: 'formal e profissional',
    friendly: 'amigável e acessível',
    persuasive: 'persuasivo e focado em vendas',
    consultive: 'consultivo e educativo'
  };

  const objectiveNames = {
    sales: 'realizar vendas',
    qualify: 'qualificar leads',
    support: 'atender e suportar clientes',
    assistant: 'ser um assistente do dia a dia, automatizando respostas'
  };

  let prompt = `Você é um assistente virtual especializado no segmento ${categoryNames[category] || category}.\n\n`;

  // Identidade
  if (sections.identity?.value) {
    prompt += `## Sobre o Negócio\n${sections.identity.value}\n\n`;
  }

  // Tom de voz
  if (sections.tone_of_voice?.selected) {
    const tone = sections.tone_of_voice.custom || toneNames[sections.tone_of_voice.selected];
    prompt += `## Tom de Comunicação\nComunique-se de forma ${tone}.\n\n`;
  }

  // Objetivo
  if (sections.objective?.selected) {
    prompt += `## Objetivo Principal\nSeu objetivo principal é ${objectiveNames[sections.objective.selected]}.\n\n`;
    
    // Detalhes do objetivo (sub-perguntas)
    if (sections.objective_details && Object.keys(sections.objective_details).length > 0) {
      prompt += `### Detalhes do Objetivo\n`;
      Object.entries(sections.objective_details).forEach(([key, value]) => {
        if (value && value.trim()) {
          prompt += `- ${value}\n`;
        }
      });
      prompt += '\n';
    }
  }

  // Regras
  if (sections.rules?.selected?.length > 0 || sections.rules?.additional) {
    prompt += `## Regras e Limites\n`;
    
    const ruleTexts = {
      no_prices: 'Não informe preços diretamente, direcione para um vendedor',
      no_competitors: 'Não mencione ou compare com concorrentes',
      no_promises: 'Não faça promessas ou compromissos sem autorização',
      business_hours: 'Respeite o horário comercial para agendamentos'
    };

    sections.rules.selected?.forEach(rule => {
      if (ruleTexts[rule]) {
        prompt += `- ${ruleTexts[rule]}\n`;
      }
    });

    if (sections.rules.additional) {
      prompt += `- ${sections.rules.additional}\n`;
    }
    prompt += '\n';
  }

  // FAQ
  if (sections.faq?.items?.length > 0) {
    prompt += `## Perguntas Frequentes\n`;
    sections.faq.items.forEach((item, index) => {
      prompt += `${index + 1}. P: ${item.question}\n   R: ${item.answer}\n`;
    });
    prompt += '\n';
  }

  // Escalonamento Humano
  if (sections.human_handoff?.value) {
    prompt += `## Quando Chamar Atendente Humano\n${sections.human_handoff.value}\n\n`;
  }

  // Personalização extra
  if (sections.customization?.value) {
    prompt += `## Instruções Adicionais\n${sections.customization.value}\n`;
  }

  return prompt;
};

/**
 * Salva o resultado do onboarding (mock)
 */
export const saveOnboardingResult = async (data) => {
  console.log('=== ONBOARDING RESULT ===');
  console.log(JSON.stringify(data, null, 2));
  console.log('========================');
  
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true, agent_id: `agent_${Date.now()}` });
    }, 500);
  });
};

export default {
  BUSINESS_CATEGORIES,
  ONBOARDING_SECTIONS,
  SECTION_ORDER,
  OBJECTIVE_QUESTIONS,
  transcribeAudio,
  buildFinalPrompt,
  saveOnboardingResult
};